import CONSTANTS from 'src/utils/constants';
import { SavingStatus } from 'src/models/common/SavingStatus';
import AllocationGroup, { AllocationGroupFormula } from 'src/models/tp-allocation/AllocationGroup';
import { DASAllocationGroup } from 'src/models/derived-allocation-sheet/DASAllocationGroup';
import {
    CreateDASAllocationFormulaRequest, DASAllocationFormula, UpdateDASAllocationFormulaRequest,
} from 'src/models/derived-allocation-sheet/DASAllocationFormula';

/**
 * Maps a Step 3 group read from the derived allocation sheet service to the TPA group model that the
 * reused TPA allocation groups reducer works on. Its formulas are read separately, per group.
 * @param group The saved group.
 * @returns The group, saved and not being edited, with no formulas.
 */
export function toAllocationGroup(group: DASAllocationGroup): AllocationGroup {
    return {
        allocationGroupId: group.allocationGroupId,
        description: group.description,
        allocationGroupDescription: group.allocationGroupDescription,
        parentEntityId: group.sheetId,
        parentEntityVersion: group.sheetVersion,
        isBeingEdited: false,
        isNew: false,
        savingStatus: SavingStatus.Saved,
        formulas: [],
    };
}

/**
 * Maps a saved Step 3 formula to the TPA group formula model.
 * @param formula The saved formula.
 * @param sequence Its 1-based position in the group.
 * @returns The formula, saved and not being edited, mapped to its company codes.
 */
export function toAllocationGroupFormula(formula: DASAllocationFormula, sequence: number): AllocationGroupFormula {
    return {
        allocationFormulaId: formula.allocationFormulaId,
        allocationGroupId: formula.allocationGroupId,
        description: formula.description,
        expression: formula.expression ?? '',
        expressionTokens: formula.expressionTokens ?? [],
        mapping: { mappingType: CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI, values: formula.mapping?.values ?? [] },
        sequence,
        value: formula.value,
        formulaOutput: formula.value ?? '',
        isBeingEdited: false,
        isNew: false,
        isEditable: true,
        valid: true,
        savingStatus: SavingStatus.Saved,
    };
}

/**
 * Builds the fields a Step 3 formula is saved with. Update replaces every field, so create and update send
 * the same ones. Company codes are trimmed and blanks dropped.
 * @param formula The formula being saved.
 * @returns Its name, formula, tokens and company mapping.
 */
function toFormulaFields(formula: AllocationGroupFormula) {
    return {
        description: (formula.description || '').trim(),
        expression: formula.expression,
        expressionTokens: formula.expressionTokens ?? [],
        mapping: {
            mappingType: CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI,
            values: (formula.mapping?.values ?? []).map((v) => v.trim()).filter((v) => v !== ''),
        },
    };
}

/** Request that creates `formula` in its group. */
export function toCreateDASAllocationFormulaRequest(sheetId: string, formula: AllocationGroupFormula): CreateDASAllocationFormulaRequest {
    return { sheetId, allocationGroupId: formula.allocationGroupId, ...toFormulaFields(formula) };
}

/** Request that replaces the saved `formula` with its current fields. */
export function toUpdateDASAllocationFormulaRequest(sheetId: string, formula: AllocationGroupFormula): UpdateDASAllocationFormulaRequest {
    return {
        sheetId, allocationGroupId: formula.allocationGroupId, allocationFormulaId: formula.allocationFormulaId, ...toFormulaFields(formula),
    };
}

/**
 * Builds an unsaved formula at the end of a group, being edited. The id is temporary and unique, so the save
 * can find the row again when the saved formula comes back.
 * @param group The group the formula is added to.
 * @returns The new formula.
 */
export function newAllocationGroupFormula(group: AllocationGroup): AllocationGroupFormula {
    return {
        allocationFormulaId: `newFormula-${Date.now()}-${(group.formulas || []).length + 1}`,
        allocationGroupId: group.allocationGroupId,
        description: '',
        expression: '',
        expressionTokens: [],
        mapping: { mappingType: CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI, values: [] },
        sequence: (group.formulas || []).length + 1,
        formulaOutput: '',
        isBeingEdited: true,
        isNew: true,
        isEditable: true,
        valid: false,
        savingStatus: SavingStatus.Unsaved,
    };
}
